/**
 * Google Maps API サービス層
 * FL-35: Distance Matrix API、Places API、Geocoding API統合
 * FL-170: Directions API統合（ルート計算・表示機能）
 *
 * 責務:
 * - Google Maps APIへのリクエスト発行
 * - API_KEY管理と検証
 * - レスポンスの正規化
 * - エラーハンドリング
 * - レート制限対応
 */

import {
  DistanceMatrixResult,
  PlaceAutocompleteResult,
  PlaceAutocompleteResponseNew,
  GeocodingResult,
  GoogleMapsServiceError,
  ApiKeyValidationResult,
  DistanceCalculationRequest,
  CaseDistanceData,
  DirectionResult,
  DirectionRoute,
  RouteLeg,
  RouteVisualizationData,
  DirectionsRequestOptions,
  StationResult,
  StationDetails,
} from '@/types/maps';

/**
 * API_KEY 検証ユーティリティ
 * 環境変数から Google Maps API_KEY を取得し、形式を検証
 */
function validateGoogleMapsApiKey(): ApiKeyValidationResult {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  // API_KEYが設定されているか確認
  if (!apiKey || apiKey.trim() === '') {
    console.error('❌ VITE_GOOGLE_MAPS_API_KEY is not configured');
    return {
      isConfigured: false,
      isValidFormat: false,
      errorMessage:
        'Google Maps API_KEY is not configured. Set VITE_GOOGLE_MAPS_API_KEY in .env.local',
    };
  }

  // Gemini API_KEY との混同を防ぐ
  if (apiKey.includes('GEMINI') || apiKey.startsWith('sk-')) {
    console.error('❌ Wrong API_KEY: Gemini key used for Google Maps');
    return {
      isConfigured: true,
      isValidFormat: false,
      errorMessage:
        'Wrong API_KEY: Gemini API key cannot be used for Google Maps. Use VITE_GOOGLE_MAPS_API_KEY instead.',
    };
  }

  // Google Maps API_KEY の形式チェック（AIza で始まる）
  if (!apiKey.startsWith('AIza')) {
    console.warn(
      '⚠️  Warning: VITE_GOOGLE_MAPS_API_KEY might have wrong format (expected: AIza...)'
    );
  }

  return {
    isConfigured: true,
    isValidFormat: true,
  };
}

/**
 * DistanceMatrixResultオブジェクト を作成（エラーハンドリング付き）
 */
function createDistanceMatrixError(status: string, errorMessage?: string): DistanceMatrixResult {
  return {
    distance_km: 0,
    duration_minutes: 0,
    status: (status || 'UNKNOWN_ERROR') as any,
    error_message: errorMessage || 'Failed to calculate distance',
  };
}

/**
 * Google Maps サービス
 * Distance Matrix API、Places API、Geocoding API の3つのメソッドを提供
 */
export const googleMapsService = {
  /**
   * Distance Matrix API を使用して、2つの住所間の距離と所要時間を計算
   *
   * @param pickup_address - 引取先住所
   * @param delivery_address - 搬入先住所
   * @returns 距離と所要時間を含む結果
   * @throws GoogleMapsServiceError
   */
  async calculateDistance(
    pickup_address: string,
    delivery_address: string
  ): Promise<DistanceMatrixResult> {
    try {
      // API_KEY 検証
      const validation = validateGoogleMapsApiKey();
      if (!validation.isConfigured || !validation.isValidFormat) {
        throw new GoogleMapsServiceError(
          'INVALID_API_KEY',
          validation.errorMessage || 'Invalid Google Maps API_KEY'
        );
      }

      // 入力値の検証
      if (!pickup_address || !delivery_address) {
        return createDistanceMatrixError(
          'INVALID_REQUEST',
          'Both pickup_address and delivery_address are required'
        );
      }

      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

      // Distance Matrix API のURL構築
      const url = new URL('https://maps.googleapis.com/maps/api/distancematrix/json');
      url.searchParams.append('origins', pickup_address);
      url.searchParams.append('destinations', delivery_address);
      url.searchParams.append('key', apiKey);
      url.searchParams.append('language', 'ja'); // 日本語で結果を取得

      // API呼び出し（タイムアウト: 10秒）
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(url.toString(), {
        method: 'GET',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // HTTP エラーチェック
      if (!response.ok) {
        console.error(
          `❌ Distance Matrix API HTTP Error: ${response.status} ${response.statusText}`
        );
        return createDistanceMatrixError(
          'REQUEST_DENIED',
          `HTTP ${response.status}: ${response.statusText}`
        );
      }

      // JSON パース
      const data = await response.json();

      // API ステータスチェック
      if (data.status !== 'OK') {
        console.warn(`⚠️  Distance Matrix API Status: ${data.status}`);
        return createDistanceMatrixError(data.status, data.error_message);
      }

      // 結果の抽出
      if (!data.rows || data.rows.length === 0) {
        return createDistanceMatrixError('ZERO_RESULTS', 'No route found');
      }

      const elements = data.rows[0]?.elements;
      if (!elements || elements.length === 0) {
        return createDistanceMatrixError('ZERO_RESULTS', 'No destination found');
      }

      const element = elements[0];

      // 要素ステータスチェック
      if (element.status !== 'OK') {
        console.warn(`⚠️  Distance Matrix Element Status: ${element.status}`);
        return createDistanceMatrixError(element.status, `Element status: ${element.status}`);
      }

      // 距離と所要時間を抽出
      const distance_meters = element.distance?.value || 0;
      const duration_seconds = element.duration?.value || 0;

      // メートル → km に変換（1桁小数に統一）
      const distance_km = Math.round((distance_meters / 1000) * 10) / 10;

      // 秒 → 分に変換（切り上げ）
      const duration_minutes = Math.ceil(duration_seconds / 60);

      return {
        distance_km,
        duration_minutes,
        status: 'OK',
        distance_meters,
        duration_seconds,
      };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.error('❌ Distance Matrix API timeout (10s)');
        return createDistanceMatrixError('OVER_QUERY_LIMIT', 'Request timeout (10s)');
      }

      if (error instanceof GoogleMapsServiceError) {
        throw error;
      }

      console.error('❌ Distance Matrix API Error:', error);
      return createDistanceMatrixError(
        'UNKNOWN_ERROR',
        error instanceof Error ? error.message : 'Unknown error'
      );
    }
  },

  /**
   * Places API (Autocomplete) を使用して、入力文字列に基づいた住所補完候補を取得
   *
   * @param input - ユーザー入力（3文字以上推奨）
   * @param options - オプション設定
   * @returns 補完候補のリスト
   * @throws GoogleMapsServiceError
   */
  async getPlaceAutocomplete(
    input: string,
    options?: {
      componentRestrictions?: string; // 'country:jp' など（新APIではテキストリクエストで使用）
      sessionToken?: string; // セッション管理用トークン
    }
  ): Promise<PlaceAutocompleteResult[]> {
    try {
      // ===== 1. 入力検証 =====
      console.log('🔍 Places Autocomplete API: 入力検証開始');
      console.debug(`  - input: "${input}"`, `length: ${input.length}`);

      // API_KEY 検証
      const validation = validateGoogleMapsApiKey();
      console.debug(
        `  - API_KEY検証:`,
        `isConfigured=${validation.isConfigured}`,
        `isValidFormat=${validation.isValidFormat}`
      );

      if (!validation.isConfigured || !validation.isValidFormat) {
        console.error('❌ API_KEY検証失敗:', validation.errorMessage);
        throw new GoogleMapsServiceError(
          'INVALID_API_KEY',
          validation.errorMessage || 'Invalid Google Maps API_KEY'
        );
      }

      // 入力値の検証
      if (!input || input.trim().length < 2) {
        console.debug(`⏭️  入力が短すぎます (${input?.trim().length || 0} < 2), 空配列を返却`);
        return [];
      }

      console.log('✅ 入力検証完了');

      // ===== 2. API呼び出し準備 =====
      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
      const url = 'https://places.googleapis.com/v1/places:autocomplete';

      // リクエストボディを構築
      const requestBody = {
        input,
        languageCode: 'ja',
        regionCode: 'JP',
        ...(options?.sessionToken && { sessionToken: options.sessionToken }),
      };

      console.log('🔄 Places API呼び出し開始');
      console.debug(`  - endpoint: ${url}`);
      console.debug(`  - method: POST`);
      console.debug(`  - headers: Content-Type=application/json, X-Goog-Api-Key=***`);
      console.debug(`  - requestBody:`, requestBody);

      // ===== 3. API呼び出し（タイムアウト: 5秒） =====
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.warn('⏱️  タイムアウト設定: 5秒');
        controller.abort();
      }, 5000);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': apiKey,
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // ===== 4. レスポンス処理 =====
      console.log(`✅ Places API レスポンス受信`);
      console.debug(
        `  - status: ${response.status}`,
        `ok: ${response.ok}`,
        `statusText: ${response.statusText}`
      );

      // HTTP エラーチェック
      if (!response.ok) {
        console.error(
          `❌ Places Autocomplete API HTTP Error: ${response.status} ${response.statusText}`
        );
        const errorData = await response.json().catch(() => ({}));
        console.error('  - errorData:', errorData);
        return [];
      }

      // JSON パース
      const data = (await response.json()) as PlaceAutocompleteResponseNew;
      console.debug(`  - responseData.suggestions length:`, data.suggestions?.length ?? 0);

      if (!data.suggestions) {
        console.warn('⚠️  レスポンスに suggestions が含まれていません');
        return [];
      }

      // ===== 5. レスポンス変換 =====
      console.log(`📝 レスポンス変換開始: ${data.suggestions.length}件の候補`);

      const predictions: PlaceAutocompleteResult[] = data.suggestions.map((suggestion, index) => {
        const prediction = suggestion.placePrediction;
        const converted = {
          place_id: prediction.placeId,
          description: prediction.text.text,
          main_text: prediction.structuredFormat?.mainText?.text || prediction.text.text,
          secondary_text: prediction.structuredFormat?.secondaryText?.text,
          types: prediction.types,
          matched_substrings: prediction.text.matches?.map(match => ({
            offset: match.startOffset,
            length: match.endOffset - match.startOffset,
          })),
        };

        console.debug(`  - [${index}] description: "${converted.description}"`);
        return converted;
      });

      console.log(`✅ レスポンス変換完了: ${predictions.length}件に変換`);
      console.debug(`  - predictions:`, predictions);

      // ===== 6. 返却 =====
      console.log(`✅ Places Autocomplete 完了: ${predictions.length}件の候補を返却`);
      return predictions;
    } catch (error) {
      // ===== エラー処理 =====
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.warn('⏰ Places Autocomplete API timeout (5s exceeded)');
        return [];
      }

      if (error instanceof GoogleMapsServiceError) {
        console.error('❌ GoogleMapsServiceError:', error.message);
        throw error;
      }

      console.error('❌ Places Autocomplete API Error:', error);
      if (error instanceof Error) {
        console.error(`  - error.name: ${error.name}`);
        console.error(`  - error.message: ${error.message}`);
      }
      return [];
    }
  },

  /**
   * Geocoding API を使用して、住所を座標（緯度・経度）に変換
   *
   * @param address - 住所文字列
   * @returns 座標情報
   * @throws GoogleMapsServiceError
   */
  async geocodeAddress(address: string): Promise<GeocodingResult | null> {
    try {
      // API_KEY 検証
      const validation = validateGoogleMapsApiKey();
      if (!validation.isConfigured || !validation.isValidFormat) {
        throw new GoogleMapsServiceError(
          'INVALID_API_KEY',
          validation.errorMessage || 'Invalid Google Maps API_KEY'
        );
      }

      // 入力値の検証
      if (!address || address.trim().length === 0) {
        return null;
      }

      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

      // Geocoding API のURL構築
      const url = new URL('https://maps.googleapis.com/maps/api/geocode/json');
      url.searchParams.append('address', address);
      url.searchParams.append('key', apiKey);
      url.searchParams.append('language', 'ja');
      url.searchParams.append('region', 'jp'); // 日本の検索結果を優先

      // API呼び出し（タイムアウト: 5秒）
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      const response = await fetch(url.toString(), {
        method: 'GET',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // HTTP エラーチェック
      if (!response.ok) {
        console.error(`❌ Geocoding API HTTP Error: ${response.status}`);
        return null;
      }

      // JSON パース
      const data = await response.json();

      // API ステータスチェック
      if (data.status !== 'OK') {
        console.warn(`⚠️  Geocoding API Status: ${data.status}`);
        return null;
      }

      // 最初の結果を抽出
      if (!data.results || data.results.length === 0) {
        console.warn('⚠️  No geocoding results found');
        return null;
      }

      const result = data.results[0];

      // 座標情報を抽出
      const { lat, lng } = result.geometry?.location || {};
      if (lat === undefined || lng === undefined) {
        return null;
      }

      return {
        latitude: lat,
        longitude: lng,
        formatted_address: result.formatted_address,
        address_components: result.address_components,
        location_type: result.geometry?.location_type,
        status: 'OK',
      };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.warn('⚠️  Geocoding API timeout (5s)');
        return null;
      }

      if (error instanceof GoogleMapsServiceError) {
        throw error;
      }

      console.error('❌ Geocoding API Error:', error);
      return null;
    }
  },

  /**
   * 距離計算リクエストから、案件に保存すべきデータを作成
   * CaseFormPage→Supabase への中間層
   *
   * @param request 距離計算リクエスト
   * @returns 案件に保存するデータ、または null（エラー時）
   */
  async prepareCaseDistanceData(
    request: DistanceCalculationRequest
  ): Promise<CaseDistanceData | null> {
    const result = await googleMapsService.calculateDistance(
      request.pickup_address,
      request.delivery_address
    );

    // エラー時は null を返す
    if (result.status !== 'OK') {
      console.error(`❌ Distance calculation failed: ${result.status}`, result.error_message);
      return null;
    }

    return {
      distance_km: result.distance_km,
      estimated_duration_minutes: result.duration_minutes,
      distance_calculated_at: new Date().toISOString(),
    };
  },

  /**
   * Directions API を使用して、2つの住所間のルート情報を取得
   * FL-170: ルート計算・可視化機能の実装
   *
   * @param origin 引取先住所
   * @param destination 搬入先住所
   * @param options オプション設定（経由地、交通情報など）
   * @returns ルート情報、またはnull（エラー時）
   * @throws GoogleMapsServiceError
   */
  async getDirections(
    origin: string,
    destination: string,
    options?: DirectionsRequestOptions
  ): Promise<DirectionResult | null> {
    try {
      // 入力値の検証
      if (!origin || !destination) {
        console.warn('⚠️  Directions API: Missing origin or destination');
        return null;
      }

      // Edge Function経由でDirections APIを呼び出し
      // (ブラウザからの直接呼び出しはCORSエラーになるため)
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

      const requestBody = {
        origin,
        destination,
        language: options?.language || 'ja',
        units: options?.units || 'metric',
        mode: options?.mode || 'driving',
        ...(options?.waypointAddresses && options.waypointAddresses.length > 0
          ? { waypoints: options.waypointAddresses }
          : {}),
        ...(options?.departureTime ? { departure_time: options.departureTime } : {}),
        ...(options?.trafficModel ? { traffic_model: options.trafficModel } : {}),
      };

      // API呼び出し（タイムアウト: 10秒）
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(`${supabaseUrl}/functions/v1/get-directions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${anonKey}`,
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // HTTP エラーチェック
      if (!response.ok) {
        console.error(`❌ Directions API HTTP Error: ${response.status} ${response.statusText}`);
        return null;
      }

      // JSON パース
      const data = await response.json();

      // API ステータスチェック
      if (data.status !== 'OK') {
        console.warn(`⚠️  Directions API Status: ${data.status}`);
        return {
          routes: [],
          status: data.status,
          error_message: data.error_message,
        };
      }

      // ルート情報を抽出
      if (!data.routes || data.routes.length === 0) {
        console.warn('⚠️  No routes found');
        return {
          routes: [],
          status: 'ZERO_RESULTS',
          error_message: 'No routes found',
        };
      }

      return {
        routes: data.routes as DirectionRoute[],
        status: 'OK',
      };
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.error('❌ Directions API timeout (10s)');
        return null;
      }

      if (error instanceof GoogleMapsServiceError) {
        throw error;
      }

      console.error('❌ Directions API Error:', error);
      return null;
    }
  },

  /**
   * Directions API の結果からルート可視化用データを生成
   * UI表示に最適化されたフォーマットに変換
   *
   * @param origin 引取先住所
   * @param destination 搬入先住所
   * @param options オプション設定
   * @returns ルート可視化データ、またはnull（エラー時）
   */
  async prepareRouteVisualizationData(
    origin: string,
    destination: string,
    options?: DirectionsRequestOptions
  ): Promise<RouteVisualizationData | null> {
    try {
      const directionResult = await googleMapsService.getDirections(origin, destination, options);

      // エラー時は null を返す
      if (
        !directionResult ||
        directionResult.status !== 'OK' ||
        directionResult.routes.length === 0
      ) {
        console.error(`❌ Route preparation failed: ${directionResult?.status || 'UNKNOWN_ERROR'}`);
        return null;
      }

      const route = directionResult.routes[0];

      // ポリラインをデコード（Google Maps API のエンコード形式）
      const polylinePoints = googleMapsService._decodePolyline(route.polyline?.points || '');

      // ステップ情報を抽出
      const steps = route.legs.flatMap((leg: RouteLeg) =>
        leg.steps.map(step => ({
          instruction: step.text_instructions || step.html_instructions.replace(/<[^>]*>/g, ''),
          distance_meters: step.distance?.value || 0,
          duration_seconds: step.duration?.value || 0,
        }))
      );

      // 合計距離と所要時間を計算（各レグの合計）
      let totalDistance = 0;
      let totalDuration = 0;
      let totalDurationInTraffic = 0;

      route.legs.forEach((leg: RouteLeg) => {
        totalDistance += leg.distance?.value || 0;
        totalDuration += leg.duration?.value || 0;
        if (leg.duration_in_traffic?.value) {
          totalDurationInTraffic += leg.duration_in_traffic.value;
        }
      });

      // トラフィック状況を判定
      const trafficDuration = totalDurationInTraffic > 0 ? totalDurationInTraffic : totalDuration;
      const trafficRatio = trafficDuration / (totalDuration || 1);

      let traffic_level: 'light' | 'moderate' | 'heavy' = 'light';
      if (trafficRatio > 1.5) {
        traffic_level = 'heavy';
      } else if (trafficRatio > 1.2) {
        traffic_level = 'moderate';
      }

      return {
        pickup_address: origin,
        delivery_address: destination,
        distance_km: Math.round((totalDistance / 1000) * 10) / 10,
        estimated_duration_minutes: Math.ceil(totalDuration / 60),
        duration_in_traffic_minutes:
          totalDurationInTraffic > 0 ? Math.ceil(totalDurationInTraffic / 60) : undefined,
        polyline_points: polylinePoints,
        traffic_level,
        calculated_at: new Date().toISOString(),
        steps,
      };
    } catch (error) {
      console.error('❌ Route visualization data preparation failed:', error);
      return null;
    }
  },

  /**
   * 最寄駅を検索（FL-270）
   * Google Maps Places Autocomplete API を使用して、駅名を入力検索
   *
   * @param query - ユーザーが入力した駅名（部分一致可）
   * @returns 駅の候補リスト
   * @throws GoogleMapsServiceError（API_KEY検証エラー時）
   */
  async searchNearestStation(query: string): Promise<StationResult[]> {
    try {
      // ===== 1. 入力検証 =====
      console.log('🔍 Station Search: 入力検証開始');
      console.debug(`  - query: "${query}"`);

      // API_KEY 検証
      const validation = validateGoogleMapsApiKey();
      console.debug(
        `  - API_KEY検証:`,
        `isConfigured=${validation.isConfigured}`,
        `isValidFormat=${validation.isValidFormat}`
      );

      if (!validation.isConfigured || !validation.isValidFormat) {
        console.error('❌ API_KEY検証失敗:', validation.errorMessage);
        throw new GoogleMapsServiceError(
          'INVALID_API_KEY',
          validation.errorMessage || 'Invalid Google Maps API_KEY'
        );
      }

      // 入力値の検証
      if (!query || query.trim().length < 2) {
        console.debug(`↩️  入力が短すぎます (${query?.trim().length || 0} < 2), 空配列を返却`);
        return [];
      }

      console.log('✅ 入力検証完了');

      // ===== 2. API呼び出し準備 =====
      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
      const url = 'https://places.googleapis.com/v1/places:autocomplete';

      // リクエストボディを構成
      const requestBody = {
        input: query,
        languageCode: 'ja',
        regionCode: 'JP',
        // 駅を検索するため、locationBias を指定（オプション）
        // ただし、ここでは全国の駅を検索対象にするため指定しない
      };

      console.log('🔄 Places API呼び出し開始');
      console.debug(`  - endpoint: ${url}`);
      console.debug(`  - method: POST`);
      console.debug(`  - requestBody:`, requestBody);

      // ===== 3. API呼び出し（タイムアウト: 5秒） =====
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.warn('⏱️  タイムアウト設定: 5秒');
        controller.abort();
      }, 5000);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Goog-Api-Key': apiKey,
        },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // ===== 4. レスポンス処理 =====
      console.log(`✅ Places API レスポンス受信`);
      console.debug(
        `  - status: ${response.status}`,
        `ok: ${response.ok}`,
        `statusText: ${response.statusText}`
      );

      // HTTP エラーチェック
      if (!response.ok) {
        console.error(
          `❌ Station Search API HTTP Error: ${response.status} ${response.statusText}`
        );
        const errorData = await response.json().catch(() => ({}));
        console.error('  - errorData:', errorData);
        // API エラーは空配列を返す（フォールバック）
        return [];
      }

      // JSON パース
      const data = (await response.json()) as PlaceAutocompleteResponseNew;
      console.debug(`  - responseData.suggestions length:`, data.suggestions?.length ?? 0);

      if (!data.suggestions) {
        console.warn('⚠️  レスポンスに suggestions が含まれていません');
        return [];
      }

      // ===== 5. レスポンス変換 =====
      console.log(`📝 レスポンス変換開始: ${data.suggestions.length}件の候補`);

      const results: StationResult[] = data.suggestions.map((suggestion, index) => {
        const prediction = suggestion.placePrediction;
        const converted: StationResult = {
          name: prediction.text.text,
          placeId: prediction.placeId,
          types: prediction.types,
          secondaryText: prediction.structuredFormat?.secondaryText?.text,
        };

        console.debug(`  - [${index}] name: "${converted.name}"`);
        return converted;
      });

      console.log(`✅ レスポンス変換完了: ${results.length}件に変換`);
      console.debug(`  - results:`, results);

      // ===== 6. 返却 =====
      console.log(`✅ Station Search 完了: ${results.length}件の駅候補を返却`);
      return results;
    } catch (error) {
      // ===== エラー処理 =====
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.warn('⏰ Station Search API timeout (5s exceeded)');
        // タイムアウトは空配列を返す（フォールバック）
        return [];
      }

      if (error instanceof GoogleMapsServiceError) {
        console.error('❌ GoogleMapsServiceError:', error.message);
        throw error;
      }

      console.error('❌ Station Search API Error:', error);
      if (error instanceof Error) {
        console.error(`  - error.name: ${error.name}`);
        console.error(`  - error.message: ${error.message}`);
      }
      // その他のエラーは空配列を返す（フォールバック）
      return [];
    }
  },

  /**
   * 駅の詳細情報を取得（FL-270）
   * Google Maps Geocoding API を使用して、placeId から緯度経度を取得
   *
   * @param placeId - Google Maps Place ID
   * @returns 駅の詳細情報（緯度・経度・住所）
   * @throws GoogleMapsServiceError（API_KEY検証エラー時）
   */
  async getStationDetails(placeId: string): Promise<StationDetails> {
    try {
      // ===== 1. 入力検証 =====
      console.log('🔍 Station Details: 入力検証開始');
      console.debug(`  - placeId: "${placeId}"`);

      // API_KEY 検証
      const validation = validateGoogleMapsApiKey();
      console.debug(
        `  - API_KEY検証:`,
        `isConfigured=${validation.isConfigured}`,
        `isValidFormat=${validation.isValidFormat}`
      );

      if (!validation.isConfigured || !validation.isValidFormat) {
        console.error('❌ API_KEY検証失敗:', validation.errorMessage);
        throw new GoogleMapsServiceError(
          'INVALID_API_KEY',
          validation.errorMessage || 'Invalid Google Maps API_KEY'
        );
      }

      // 入力値の検証
      if (!placeId || placeId.trim().length === 0) {
        console.debug('❌ placeId が空です');
        return {
          name: '',
          placeId: '',
          latitude: 0,
          longitude: 0,
          formattedAddress: '',
          status: 'INVALID_REQUEST',
          error_message: 'placeId is required',
        };
      }

      console.log('✅ 入力検証完了');

      const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

      // ===== 2. Geocoding API URL 構成 =====
      // Note: placeId を直接 Geocoding API に渡す方法は限定的なため、
      // 実際には Places API の Place Details エンドポイントを使用することが推奨される。
      // ここでは、searchNearestStation() で取得した name を使用して geocodeAddress() を呼び出す
      // または、別途 Place Details API を呼び出す必要がある。

      const url = new URL('https://maps.googleapis.com/maps/api/geocode/json');
      url.searchParams.append('place_id', placeId);
      url.searchParams.append('key', apiKey);
      url.searchParams.append('language', 'ja');

      console.log('🔄 Geocoding API 呼び出し開始');
      console.debug(`  - endpoint: ${url}`);
      console.debug(`  - method: GET`);

      // ===== 3. API呼び出し（タイムアウト: 5秒） =====
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.warn('⏱️  タイムアウト設定: 5秒');
        controller.abort();
      }, 5000);

      const response = await fetch(url.toString(), {
        method: 'GET',
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // ===== 4. レスポンス処理 =====
      console.log(`✅ Geocoding API レスポンス受信`);
      console.debug(
        `  - status: ${response.status}`,
        `ok: ${response.ok}`,
        `statusText: ${response.statusText}`
      );

      // HTTP エラーチェック
      if (!response.ok) {
        console.error(`❌ Geocoding API HTTP Error: ${response.status}`);
        return {
          name: '',
          placeId,
          latitude: 0,
          longitude: 0,
          formattedAddress: '',
          status: 'REQUEST_DENIED',
          error_message: `HTTP ${response.status}: ${response.statusText}`,
        };
      }

      // JSON パース
      const data = await response.json();
      console.debug(`  - responseData.results length:`, data.results?.length ?? 0);

      // API ステータスチェック
      if (data.status !== 'OK') {
        console.warn(`⚠️  Geocoding API Status: ${data.status}`);
        return {
          name: '',
          placeId,
          latitude: 0,
          longitude: 0,
          formattedAddress: '',
          status: data.status,
          error_message: data.error_message || data.status,
        };
      }

      // 最初の結果を抽出
      if (!data.results || data.results.length === 0) {
        console.warn('⚠️  No geocoding results found');
        return {
          name: '',
          placeId,
          latitude: 0,
          longitude: 0,
          formattedAddress: '',
          status: 'ZERO_RESULTS',
          error_message: 'No results found',
        };
      }

      const result = data.results[0];

      // ===== 5. 座標情報を抽出 =====
      const { lat, lng } = result.geometry?.location || {};
      if (lat === undefined || lng === undefined) {
        console.warn('⚠️  Latitude/longitude not found in result');
        return {
          name: '',
          placeId,
          latitude: 0,
          longitude: 0,
          formattedAddress: result.formatted_address || '',
          status: 'UNKNOWN_ERROR',
          error_message: 'Latitude/longitude not found',
        };
      }

      // ===== 6. レスポンス変換 =====
      console.log('📝 レスポンス変換開始');

      // アドレスコンポーネントを変換
      const addressComponents = result.address_components?.map((component: any) => ({
        longName: component.long_name,
        shortName: component.short_name,
        types: component.types,
      }));

      const stationDetails: StationDetails = {
        name: result.formatted_address.split(',')[0] || '', // 最初の部分を駅名として使用
        placeId,
        latitude: lat,
        longitude: lng,
        formattedAddress: result.formatted_address,
        addressComponents,
        status: 'OK',
      };

      console.log(`✅ レスポンス変換完了`);
      console.debug(`  - stationDetails:`, stationDetails);

      // ===== 7. 返却 =====
      console.log(`✅ Station Details 取得完了`);
      return stationDetails;
    } catch (error) {
      // ===== エラー処理 =====
      if (error instanceof DOMException && error.name === 'AbortError') {
        console.warn('⏰ Geocoding API timeout (5s exceeded)');
        return {
          name: '',
          placeId: '',
          latitude: 0,
          longitude: 0,
          formattedAddress: '',
          status: 'UNKNOWN_ERROR',
          error_message: 'Request timeout',
        };
      }

      if (error instanceof GoogleMapsServiceError) {
        console.error('❌ GoogleMapsServiceError:', error.message);
        throw error;
      }

      console.error('❌ Station Details Error:', error);
      if (error instanceof Error) {
        console.error(`  - error.name: ${error.name}`);
        console.error(`  - error.message: ${error.message}`);
      }

      return {
        name: '',
        placeId: '',
        latitude: 0,
        longitude: 0,
        formattedAddress: '',
        status: 'UNKNOWN_ERROR',
        error_message: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  },

  /**
   * Google Maps API エンコードポリラインをデコード
   * ポリラインポイント（緯度経度配列）に変換
   *
   * @param encoded エンコードされたポリラインテキスト
   * @returns { lat, lng } 配列
   * @ref https://developers.google.com/maps/documentation/utilities/polylinealgorithm
   */
  _decodePolyline(encoded: string): Array<{ lat: number; lng: number }> {
    const points: Array<{ lat: number; lng: number }> = [];
    let index = 0;
    let lat = 0;
    let lng = 0;

    while (index < encoded.length) {
      let result = 0;
      let shift = 0;

      // 緯度をデコード
      let byte = 0;
      do {
        byte = encoded.charCodeAt(index++) - 63;
        result |= (byte & 0x1f) << shift;
        shift += 5;
      } while (byte >= 0x20);

      const dlat = result & 1 ? ~(result >> 1) : result >> 1;
      lat += dlat;

      // 経度をデコード
      result = 0;
      shift = 0;
      do {
        byte = encoded.charCodeAt(index++) - 63;
        result |= (byte & 0x1f) << shift;
        shift += 5;
      } while (byte >= 0x20);

      const dlng = result & 1 ? ~(result >> 1) : result >> 1;
      lng += dlng;

      points.push({
        lat: lat / 1e5,
        lng: lng / 1e5,
      });
    }

    return points;
  },
};
